import React, { useState, useEffect, useRef } from "react";
import { Modal } from "../Modal/Modal";
import { CommissionContract, CommissionComment } from "../../api/ComissionContractsAPI";
import ComissionContractsAPI from "../../api/ComissionContractsAPI";
import ConfigAPI from "../../api/ConfigAPI";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import { CommissionStatus } from "../../types/commission-statuses";
import { toastService } from "../../services/ToastService";
import { useTranslation } from "react-i18next";
import CommissionPaymentPlanManager, { CommissionPaymentPlanManagerRef } from "../CommissionPaymentPlan/CommissionPaymentPlanManager";
import { useCommissionContractTemplate } from "../../hooks/useCommissionContractTemplate";
import CommissionPaymentPlansAPI, { toFrontend as commissionPlanToFrontend } from "../../api/CommissionPaymentPlansAPI";
import SlideOver from "../SlideOver/SlideOver";
import FilesAPI from "../../api/FilesAPI";
import { urlAndS3Encode, isLargeFile } from "../../shared/fileUtils";
import { simplifyUrl } from "../../shared/urlUtils";
import { PaymentPlanItem, PaymentConcept } from "../../types/payment-plan";
import { CommissionPaymentPlan } from "../../types/commissionPaymentPlan";
import { TabV2, TabsList, TabsTrigger, TabsContent } from "../TabV2/TabV2";
import StoreCommissionPanelHeader from "./sections/StoreCommissionPanelHeader";
import GeneralTabSection from "./sections/GeneralTabSection";
import ItemTabSection from "./sections/ItemTabSection";
import PaymentsTabSection from "./sections/PaymentsTabSection";
import CommentsTabSection from "./sections/CommentsTabSection";
import CommissionContractOffersAPI, { CommissionContractOfferDto } from "../../api/CommissionContractOffersAPI";
interface StoreCommissionPanelProps {
  commission: CommissionContract;
  onClose: () => void;
  isEdit?: boolean;
  onSave?: (commission: CommissionContract) => void;
}

function StoreCommissionPanel({ commission, onClose, isEdit, onSave }: StoreCommissionPanelProps) {
  const { t } = useTranslation();
  const { contractTemplate } = useCommissionContractTemplate();
  const [comment, setComment] = useState('');
  const [isEditing, setIsEditing] = useState(isEdit);
  const [comments, setComments] = useState<CommissionComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [storeName, setStoreName] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [showPaymentPlanManager, setShowPaymentPlanManager] = useState(false);
  const [, setIsEditMode] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState<CommissionPaymentPlan | undefined>((commission as any).paymentPlan);
  const paymentPlanManagerRef = useRef<CommissionPaymentPlanManagerRef>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [showDamagesNotes, setShowDamagesNotes] = useState(false);
  const [enabledOffers, setEnabledOffers] = useState<CommissionContractOfferDto[]>([]);
  const [isDownloadingRequestSummary, setIsDownloadingRequestSummary] = useState(false);

  useEffect(() => {
    setIsEditing(isEdit);
  }, [isEdit]);

  const [form, setForm] = useState<CommissionContract>({ ...commission });

  useEffect(() => {
    setForm({ ...commission });
  }, [commission]);

  useEffect(() => {
    if (commission.id) {
      fetchComments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commission.id]);

  useEffect(() => {
    const loadOffers = async () => {
      try {
        const response = await CommissionContractOffersAPI.list();
        const offers = (response.data ?? []).filter(
          (o) => o.enabled !== false && o.id != null
        );
        setEnabledOffers(offers);
        if (!commission.id && offers.length > 0) {
          setForm((prev) =>
            prev.commissionContractOfferId != null
              ? prev
              : { ...prev, commissionContractOfferId: offers[0].id }
          );
        }
      } catch (error) {
        console.error("Failed to load commission contract offers", { error });
      }
    };
    loadOffers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchStoreName = async () => {
      try {
        const response = await ConfigAPI.get();
        setStoreName(response.data.brandName || '');
      } catch (error) {
        console.error('Failed to fetch store name:', error);
      }
    };
    fetchStoreName();
  }, []);

  useEffect(() => {
    const fetchPaymentPlan = async () => {
      if (!commission.id) return;
        const resp = await CommissionPaymentPlansAPI.get(commission.id);
        if (resp.data) {
          setPaymentPlan(commissionPlanToFrontend(resp.data));
        }
    };
    fetchPaymentPlan();
  }, [commission.id]);

  const shareLink = `${window.location.protocol}//${window.location.host}/drops/track/${commission.trackingId ?? ''}`;

  const latestContract = commission.contracts && commission.contracts.length > 0
    ? [...commission.contracts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
    : null;

  const fetchComments = async () => {
    if (!commission.id) return;
    try {
      const response = await ComissionContractsAPI.getComments(commission.id);
      setComments(response.data);
    } catch (error) {
      toastService.showToast(t('store_commission_panel.toast.comments_load_error') || 'Failed to load comments', 'error');
    }
  };

  const handleDownloadRequestSummary = async () => {
    if (!commission.id) return;
    setIsDownloadingRequestSummary(true);
    try {
      const response = await ComissionContractsAPI.downloadRequestSummaryPdf(commission.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const rawNumber = commission.contractNumber?.trim();
      const safeNumber = rawNumber
        ? rawNumber.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/(^-+|-+$)/g, '')
        : '';
      const fileName = safeNumber ? `commission-request-${safeNumber}.pdf` : 'commission-request.pdf';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download commission request summary PDF', { commissionId: commission.id, error });
      toastService.showToast(
        t('store_commission_panel.errors.failed_to_download_request_summary') || 'Failed to download request summary',
        'error'
      );
    } finally {
      setIsDownloadingRequestSummary(false);
    }
  };

  const handleAddComment = async () => {
    if (!commission.id || !comment.trim()) return;
    
    setIsLoading(true);
    try {
      await ComissionContractsAPI.addComment(commission.id, {
        comment: comment.trim(),
        writtenBy: storeName
      });
      setComment('');
      await fetchComments();
      toastService.showToast(t('store_commission_panel.toast.comment_added') || 'Comment added', 'success');
    } catch (error) {
      toastService.showToast(t('store_commission_panel.toast.comment_error') || 'Failed to add comment', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(form);
      onClose();
    }
  };

  const refreshPaymentData = async () => {
    if (!commission.id) return;
    try {
      const response = await ComissionContractsAPI.getById(commission.id);
      const updatedCommission = response.data;
      
      setForm(prev => ({
        ...prev,
        payment: updatedCommission.payment,
        paymentPlan: updatedCommission.paymentPlan
      }));
    } catch (error) {
      console.error('Failed to refresh payment data:', error);
    }
  };

  const handlePaymentPlanSave = async (newPaymentPlan: CommissionPaymentPlan): Promise<void> => {
    try {
      if (!commission.id) return;
      let updatedPlanData: CommissionPaymentPlan;
      if (!paymentPlan || !paymentPlan.id) {
        const resp = await CommissionPaymentPlansAPI.create(commission.id, newPaymentPlan);
        updatedPlanData = commissionPlanToFrontend(resp.data);
        setPaymentPlan(updatedPlanData);
      } else {
        const resp = await CommissionPaymentPlansAPI.update(commission.id, newPaymentPlan);
        updatedPlanData = commissionPlanToFrontend(resp.data);
        setPaymentPlan(updatedPlanData);
      }
      setShowPaymentPlanManager(false);
      setIsEditMode(false);
      await refreshPaymentData();
      toastService.showToast(t("store_commission_panel.payment_plan_updated") || "Payment plan updated successfully", "success");
    } catch (e: any) {
      toastService.showToast(t("payment_plan.errors.failed_to_save") || 'Failed to save payment plan', 'error');
    }
  };

  const handlePaymentPlanCancel = () => {
    setShowPaymentPlanManager(false);
    setIsEditMode(false);
  };

  const handlePaymentPlanEdit = () => {
    setIsEditMode(true);
    setShowPaymentPlanManager(true);
  };


  const handleAddRequiredPayment = () => {
    const selectedOffer = enabledOffers.find((o) => o.id === form.commissionContractOfferId);
    setPaymentPlan(prev => {
      const renewalPrice =
        selectedOffer?.renewalFeePrice ?? contractTemplate?.renewalFee?.price ?? 0;
      const renewalCurrency =
        selectedOffer?.renewalFeeCurrency || contractTemplate?.renewalFee?.currency || "EUR";
      const currency = renewalCurrency || prev?.currency || commission.desiredSalePriceCurrency || "EUR";
      const base: CommissionPaymentPlan = prev || {
        id: null, // Use null instead of empty string
        contractId: commission.id || 0,
        items: [],
        totalAmount: 0,
        currency,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const newItem: PaymentPlanItem = {
        id: null,
        concept: PaymentConcept.RENEWAL_FEE,
        amount: renewalPrice,
        currency,
        status: 'NOT_PAID' as any,
        isRequired: true,
        validForDays: selectedOffer?.saleDuration ?? contractTemplate?.saleDuration,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        description: ''
      };
      const updatedPlan = { ...base, items: [...base.items, newItem], updatedAt: new Date().toISOString() };
      return updatedPlan;
    });
    
    setIsEditMode(true);
    setShowPaymentPlanManager(true);
  };

  const handleDeletePaymentSplit = async (index: number) => {
    if (!paymentPlan || !commission.id) return;
    
    try {
      const updatedItems = paymentPlan.items.filter((_, i) => i !== index);
      const updatedPlan: CommissionPaymentPlan = {
        ...paymentPlan,
        items: updatedItems,
        totalAmount: updatedItems.reduce((sum, item) => sum + item.amount, 0),
        updatedAt: new Date().toISOString()
      };
      
      setPaymentPlan(updatedPlan);
      
      const resp = await CommissionPaymentPlansAPI.update(commission.id, updatedPlan);
      const updatedPlanData = commissionPlanToFrontend(resp.data);
      setPaymentPlan(updatedPlanData);
      await refreshPaymentData();
    } catch (e: any) {
      setPaymentPlan(paymentPlan);
      toastService.showToast(t("global.error") || "Failed to delete payment split", "error");
    }
  };

  const removePhoto = (field: 'attachments', fileName: string) => {
    if (!form) return;
    setForm({
      ...form,
      [field]: (form[field] || []).filter((f: string) => f !== fileName)
    });
  };

  const handleFileChange = async (field: 'attachments', files: File[]) => {
    if (!form || !commission.trackingId) return;
    
    setIsUploading(true);
    try {
      const uploadedUrls: string[] = [];
      
      for (const file of files) {
        try {
          const fileWithS3Encoding = urlAndS3Encode(file.name);
          const displayFileName = decodeURI(fileWithS3Encoding);
          
          let fileUrl: string;
          
          if (isLargeFile(file)) {
            const uploadUrlResponse = await FilesAPI.getUploadUrl(fileWithS3Encoding, 'commission-contracts', commission.trackingId);
            const uploadUrl = uploadUrlResponse.data.url;
            const response = await FilesAPI.uploadHeavyFile(uploadUrl, file, fileWithS3Encoding);
            if (response.status === 200) {
              fileUrl = simplifyUrl(uploadUrl);
            } else {
              throw new Error('Failed to upload large file');
            }
          } else {
            // Use regular upload for smaller files
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await FilesAPI.upload(`/v1/files/commission-contracts/uploadV2?prefixId=${commission.trackingId}`, fileWithS3Encoding, formData);
            fileUrl = response.data.url;
          }
          
          uploadedUrls.push(fileUrl);
          toastService.showToast(`${t("toast.success.add_file") || 'File added'}: ${displayFileName}`, 'success');
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          toastService.showToast(`${t("toast.errors.add_file") || 'Failed to add file'}: ${file.name}`, 'error');
        }
      }
      
      // Update form with new file URLs
      if (uploadedUrls.length > 0) {
        setForm({
          ...form,
          [field]: [...(form[field] || []), ...uploadedUrls]
        });
      }
    } catch (error) {
      console.error('Error processing files:', error);
      toastService.showToast(t('store_commission_panel.errors.failed_to_upload_files') || 'Failed to upload files', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (field: keyof CommissionContract) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleNumberChange = (field: keyof CommissionContract) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const offerDisplayName = (isEditing ? form.offerNameSnapshot : commission.offerNameSnapshot)?.trim();
  const showOfferChangeHint =
    !!isEditing &&
    !!commission.id &&
    form.commissionContractOfferId != null &&
    form.commissionContractOfferId !== commission.commissionContractOfferId &&
    !!paymentPlan &&
    paymentPlan.items.length > 0;

  const handleSelectOffer = (offerId: number) => {
    const offer = enabledOffers.find((o) => o.id === offerId);
    setForm((prev) => ({
      ...prev,
      commissionContractOfferId: offerId,
      offerNameSnapshot: offer?.name ?? prev.offerNameSnapshot,
    }));
  };

  const showPdfNotice =
    !!commission.trackingId && commission.status !== CommissionStatus.ACCEPTED;
  const hasCommissionId = !!commission.id;
  const tabGridClass = hasCommissionId
    ? "grid-cols-2 sm:grid-cols-3"
    : "grid-cols-2";

  const handleFormUpdate = (update: Partial<CommissionContract>) => {
    setForm((prev) => ({ ...prev, ...update }));
  };

  const handleAddAddon = () => {
    const newAddon = {
      name: "",
      description: "",
      cost: 0,
      currency: "EUR",
    };
    setForm((prev) => ({
      ...prev,
      addons: [...(prev.addons || []), newAddon],
    }));
  };

  const handleRemoveAddon = (index: number) => {
    setForm((prev) => ({
      ...prev,
      addons: (prev.addons || []).filter((_, i) => i !== index),
    }));
  };

  const handleUpdateAddon = (
    index: number,
    field: "name" | "description" | "cost" | "currency",
    value: string | number
  ) => {
    setForm((prev) => {
      const newAddons = [...(prev.addons || [])];
      if (field === "name") newAddons[index].name = value as string;
      else if (field === "description") newAddons[index].description = value as string;
      else if (field === "cost") newAddons[index].cost = value as number;
      else if (field === "currency") newAddons[index].currency = value as string;
      return { ...prev, addons: newAddons };
    });
  };

  return (
    <Modal modalTitle={t("store_commission_panel.modal_title") || "Commission details"} canClose={true} onClose={onClose}>
      <>
        <div>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 space-y-4 overflow-y-auto max-h-[80vh] pr-2">
            {commission.trackingId && (
              <StoreCommissionPanelHeader
                shareLink={shareLink}
                pdfUrl={latestContract?.pdf}
                showRequestSummary={!!commission.id}
                onDownloadRequestSummary={handleDownloadRequestSummary}
                isDownloadingRequestSummary={isDownloadingRequestSummary}
              />
            )}

            <TabV2 defaultValue="general" className="w-full">
              <TabsList className={`grid w-full ${tabGridClass} py-[15px]`}>
                <TabsTrigger value="general">
                  {t("store_commission_panel.tabs.general") || "General"}
                </TabsTrigger>
                <TabsTrigger value="item">
                  {t("store_commission_panel.tabs.item") || t("store_commission_panel.item_data_title") || "Item"}
                </TabsTrigger>
                {hasCommissionId && (
                  <TabsTrigger value="payments">
                    {t("store_commission_panel.tabs.payments") || t("store_commission_panel.payments_title") || "Payments"}
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="general" className="space-y-4 pt-2">
                <GeneralTabSection
                  form={form}
                  commission={commission}
                  isEditing={!!isEditing}
                  offerDisplayName={offerDisplayName}
                  enabledOffers={enabledOffers}
                  showOfferChangeHint={showOfferChangeHint}
                  showPdfNotice={showPdfNotice}
                  onInputChange={handleInputChange}
                  onPhoneChange={(value) => setForm((prev) => ({ ...prev, bridePhone: value }))}
                  onEndDateChange={(date) =>
                    setForm((prev) => ({
                      ...prev,
                      endDate: date ? date.toISOString().split("T")[0] : "",
                    }))
                  }
                  onSelectOffer={handleSelectOffer}
                />
              </TabsContent>

              <TabsContent value="item" className="space-y-4 pt-2">
                <ItemTabSection
                  form={form}
                  commission={commission}
                  isEditing={!!isEditing}
                  showNotes={showNotes}
                  showDamagesNotes={showDamagesNotes}
                  isUploading={isUploading}
                  onToggleNotes={() => setShowNotes(!showNotes)}
                  onToggleDamagesNotes={() => setShowDamagesNotes(!showDamagesNotes)}
                  onInputChange={handleInputChange}
                  onNumberChange={handleNumberChange}
                  onFormUpdate={handleFormUpdate}
                  onToggleItemReceived={() =>
                    setForm((prev) => ({ ...prev, itemReceived: !prev.itemReceived }))
                  }
                  onFileChange={(files) => handleFileChange("attachments", files)}
                  onRemoveAttachment={(url) => removePhoto("attachments", url)}
                  onAddAddon={handleAddAddon}
                  onRemoveAddon={handleRemoveAddon}
                  onUpdateAddon={handleUpdateAddon}
                />
              </TabsContent>

              {hasCommissionId && (
                <TabsContent value="payments" className="space-y-4 pt-2">
                  <PaymentsTabSection
                    form={form}
                    isEditing={!!isEditing}
                    paymentPlan={paymentPlan}
                    onInputChange={handleInputChange}
                    onAddRequiredPayment={handleAddRequiredPayment}
                    onPaymentPlanEdit={handlePaymentPlanEdit}
                    onDeletePaymentSplit={handleDeletePaymentSplit}
                  />
                </TabsContent>
              )}

            </TabV2>
            </div>
            {hasCommissionId && (
              <div className="w-full md:w-80 flex flex-col">
                <CommentsTabSection
                  comments={comments}
                  commentDraft={comment}
                  storeName={storeName}
                  isLoading={isLoading}
                  onCommentDraftChange={setComment}
                  onAddComment={handleAddComment}
                />
              </div>
            )}
          </div>
          {isEditing && (
            <div className="mt-4 flex justify-center">
              <PrimaryButton text={t("store_commission_panel.save_button") || "Save"} fn={handleSave} />
            </div>
          )}
        </div>
        {/* Commission Payment Plan Manager SlideOver */}
        <SlideOver
          isOpen={showPaymentPlanManager}
          setIsOpen={setShowPaymentPlanManager}
          aboveModal
          title={t('commission_payment_plan.title') || 'Commission Payment Plan'}
          primaryButton={{
            label: t('global.save') || 'Save',
            onClick: () => {
              paymentPlanManagerRef.current?.save();
            }
          }}
          secondaryButton={{
            label: t('global.cancel') || 'Cancel',
            onClick: handlePaymentPlanCancel
          }}
        >
          <CommissionPaymentPlanManager
            ref={paymentPlanManagerRef}
            paymentPlan={paymentPlan}
            onSave={handlePaymentPlanSave}
            onCancel={handlePaymentPlanCancel}
            isEditable={isEditing}
            contractId={commission.id}
          />
        </SlideOver>
        
      </>
    </Modal>
  );
}

export default StoreCommissionPanel; 